import { useQuery } from "@tanstack/react-query";
import { DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Panel, Section, Stat, StateView, useAuth } from "@ozpanel/ui";

// Per-module cluster state for the storage/database/proxy modules.
//
// These attach to their module's existing page through MODULE_OPS rather than getting nav
// entries of their own — a module already appears in the sidebar from its manifest, and adding
// a second entry with the same name is what put two "Cluster" and two "Kernel & network tuning"
// rows in the nav earlier today.
//
// The recurring theme is that "the service is running" and "the service is doing its job" are
// different questions, and only the second one matters: a Galera node can be up and
// non-Primary, a Redis replica can be connected with a broken link, a Caddy instance can hold
// certificates it never loads.

function useJSON<T>(key: string, path: string, refetch?: number) {
  const { client } = useAuth();
  return useQuery({ queryKey: [key], queryFn: () => client.apiFetch<T>(path), refetchInterval: refetch });
}

/** MariaDB / Galera membership. */
export function MariadbCluster() {
  interface C {
    mode: string;
    connected: boolean;
    ready: boolean;
    cluster_status: string;
    local_state: string;
    cluster_size: number;
    // null = could not be read (the reason is under unread) — never shown as "off" or as empty.
    cluster_address: string | null;
    file_cluster_address: string | null;
    ssl_configured: boolean | null;
    ssl_running: boolean | null;
    unread?: Record<string, string>;
  }
  const q = useJSON<C>("mariadb-cluster", "/mariadb/cluster", 20000);
  const d = q.data;
  return (
    <Section title="Galera cluster" hint={d?.mode}>
      <StateView loading={q.isLoading} error={q.error} errorContext="the Galera cluster">
        {d && (
          <>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {/* Primary is the only state that accepts writes. A node can be Synced, connected
                  and healthy-looking while non-Primary, in which case every write fails. */}
              <StatusPill tone={d.cluster_status === "Primary" ? "ok" : "bad"}>
                {d.cluster_status || "unknown"}
              </StatusPill>
              <StatusPill tone={d.local_state === "Synced" ? "ok" : "warn"}>{d.local_state}</StatusPill>
              <StatusPill tone={d.ready ? "ok" : "bad"}>{d.ready ? "accepting queries" : "NOT ready"}</StatusPill>
              {/* Configured and running are separate: a file that sets replication TLS on a
                  daemon that never reloaded is TLS nobody is using. */}
              {d.ssl_running == null ? (
                <StatusPill tone="neutral">replication TLS: not read</StatusPill>
              ) : (
                <StatusPill tone={d.ssl_running ? "ok" : d.ssl_configured ? "warn" : "neutral"}>
                  {d.ssl_running ? "replication TLS active" : d.ssl_configured ? "TLS configured, NOT running" : "replication in the clear"}
                </StatusPill>
              )}
            </div>
            <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Stat label="Cluster size" value={String(d.cluster_size)} hint="counts garbd" />
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12.5px]">
              <dt className="text-muted">Running address</dt>
              <dd className="break-all font-mono text-[11.5px]">{d.cluster_address ?? `not read: ${d.unread?.cluster_address ?? ""}`}</dd>
              <dt className="text-muted">On disk</dt>
              <dd className="break-all font-mono text-[11.5px]">{d.file_cluster_address ?? `not read: ${d.unread?.file_cluster_address ?? ""}`}</dd>
            </dl>
            {d.cluster_address != null && d.file_cluster_address != null && d.cluster_address !== d.file_cluster_address && (
              // The running value and the file disagree, so the next restart changes membership.
              <Panel className="mt-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">
                The address in the config file differs from the one in force. This node will join a different set
                on its next restart than the one it is in now.
              </Panel>
            )}
          </>
        )}
      </StateView>
    </Section>
  );
}

/** Redis replication + Sentinel. */
export function RedisTopology() {
  interface Master {
    name: string;
    ip: string;
    port: number;
    flags: string;
    can_failover: boolean;
    other_sentinels: number;
    down_after_ms: number;
    last_ok_ping_ms: number;
    failover_detail?: string;
  }
  interface T {
    node: {
      role: string;
      master_host?: string;
      master_link?: string;
      connected_replicas?: number;
      seconds_since_sync?: number;
      master_failover_state?: string;
    };
    replicas: { addr?: string; state?: string }[] | null;
    sentinel_masters: Master[] | null;
  }
  const q = useJSON<T>("redis-topology", "/redis/topology", 20000);
  const d = q.data;
  return (
    <Section title="Replication & Sentinel">
      <StateView loading={q.isLoading} error={q.error} errorContext="the Redis topology">
        {d && (
          <>
            <div className="mb-3 flex flex-wrap gap-1.5">
              <StatusPill tone={d.node.role === "master" ? "accent" : "neutral"}>{d.node.role}</StatusPill>
              {d.node.role !== "master" && (
                // A replica whose link is down still answers reads — with stale data, silently.
                <StatusPill tone={d.node.master_link === "up" ? "ok" : "bad"}>
                  link to master {d.node.master_link || "unknown"}
                </StatusPill>
              )}
              <StatusPill tone="neutral">{d.node.connected_replicas ?? 0} replica(s)</StatusPill>
            </div>
            {(d.sentinel_masters ?? []).map((m) => (
              <div key={m.name} className="mb-2 rounded-lg border border-line p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[12.5px] font-medium">{m.name}</span>
                  <span className="font-mono text-[11.5px] text-muted">{m.ip}:{m.port}</span>
                  {/* Sentinels agreeing is not the same as sentinels being ABLE to act: a stale
                      register raises the votes needed above the number alive, and every
                      indicator still reads healthy. */}
                  <StatusPill tone={m.can_failover ? "ok" : "bad"}>
                    {m.can_failover ? "can fail over" : "CANNOT fail over"}
                  </StatusPill>
                  <StatusPill tone={m.flags === "master" ? "ok" : "warn"}>{m.flags}</StatusPill>
                  <span className="text-[11.5px] text-muted">{m.other_sentinels} other sentinel(s)</span>
                </div>
                {m.failover_detail && <p className="mt-1 text-[11.5px] leading-relaxed text-muted">{m.failover_detail}</p>}
              </div>
            ))}
          </>
        )}
      </StateView>
    </Section>
  );
}

/** GlusterFS health + volumes. */
export function GlusterHealth() {
  interface H {
    verdict: string;
    glusterd: boolean;
    volumes: number;
    checks: { check: string; status: string; detail?: string }[] | null;
  }
  const q = useJSON<H>("gluster-health", "/glusterfs/health", 30000);
  const d = q.data;
  const cols: DataListColumn<{ check: string; status: string; detail?: string }>[] = [
    {
      key: "status",
      label: "",
      value: (c) => c.status,
      render: (c) => (
        <StatusPill tone={c.status === "ok" ? "ok" : c.status === "warn" ? "warn" : "bad"}>{c.status}</StatusPill>
      ),
    },
    { key: "check", label: "Check", sortable: true, value: (c) => c.check },
    { key: "detail", label: "Detail", value: (c) => c.detail || "", render: (c) => <span className="text-[11.5px] text-muted">{c.detail}</span> },
  ];
  return (
    <Section title="Storage health" hint={d ? `${d.volumes} volume(s)` : undefined} bodyClassName="p-0">
      <div className="p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="storage health">
          {d && (
            <>
              <div className="mb-3 flex flex-wrap gap-1.5">
                <StatusPill tone={d.verdict === "ok" ? "ok" : d.verdict === "warn" ? "warn" : "bad"}>{d.verdict}</StatusPill>
                <StatusPill tone={d.glusterd ? "ok" : "bad"}>glusterd {d.glusterd ? "running" : "down"}</StatusPill>
              </div>
              {/* Mounted is not replicated, and serving is not the same as mounted. The checks
                  are listed individually because the single verdict cannot say which. */}
              <DataList items={d.checks ?? []} columns={cols} getRowKey={(c) => c.check} emptyMessage="No checks reported." />
            </>
          )}
        </StateView>
      </div>
    </Section>
  );
}

/** PostgreSQL cluster (repmgr). */
export function PostgresCluster() {
  interface N {
    id: number;
    name: string;
    role: string;
    priority: number;
    active: boolean;
    conninfo?: string;
  }
  interface C {
    configured: boolean;
    single_node: boolean;
    total: number;
    nodes: N[] | null;
    this_node: { role?: string };
  }
  const q = useJSON<C>("pg-cluster", "/postgresql/cluster", 30000);
  const d = q.data;
  const cols: DataListColumn<N>[] = [
    { key: "name", label: "Node", sortable: true },
    {
      key: "role",
      label: "Role",
      sortable: true,
      render: (n) => <StatusPill tone={n.role === "primary" ? "accent" : "neutral"}>{n.role}</StatusPill>,
    },
    {
      key: "active",
      label: "State",
      sortable: true,
      value: (n) => (n.active ? "active" : "down"),
      render: (n) => <StatusPill tone={n.active ? "ok" : "bad"}>{n.active ? "active" : "not responding"}</StatusPill>,
    },
    { key: "priority", label: "Failover priority", sortable: true, value: (n) => n.priority },
  ];
  return (
    <Section title="PostgreSQL cluster" hint={d?.this_node?.role} bodyClassName="p-0">
      <div className="p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="the PostgreSQL cluster">
          {d && (
            <>
              {!d.configured ? (
                <p className="text-[12.5px] text-muted">Replication is not configured — this node stands alone.</p>
              ) : (
                <>
                  {d.single_node && (
                    <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">
                      Only one node is registered, so there is nothing to fail over to. Replication is configured but
                      provides no redundancy.
                    </Panel>
                  )}
                  <DataList items={d.nodes ?? []} columns={cols} getRowKey={(n) => String(n.id)} emptyMessage="No nodes registered." />
                </>
              )}
            </>
          )}
        </StateView>
      </div>
    </Section>
  );
}

/** PgBouncer pools — what is actually connected through the pooler, and what is waiting. */
export function PgbouncerPools() {
  interface P {
    count: number;
    pools: Record<string, string>[] | null;
  }
  const q = useJSON<P>("pgb-pools", "/pgbouncer/pools", 20000);
  const d = q.data;

  // This used to render col0..colN as-is, with a comment explaining that inventing names for
  // positional columns would be worse than showing raw ones. That was the right call and the
  // wrong fix: the names were being thrown away at the source, because the module ran psql with
  // `-t`, which suppresses the header row PgBouncer puts them in. The server now keeps it.
  //
  // Only the columns worth a person's attention are given a label and an explanation; everything
  // else PgBouncer returns is still rendered, under its own name, because a column this build has
  // never heard of should appear rather than be silently dropped — SHOW POOLS gained three columns
  // across 1.21 and 1.23.
  const explained: Record<string, { label: string; help?: string }> = {
    database: { label: "Database" },
    user: { label: "User" },
    cl_active: { label: "Clients active", help: "Connected and using a server connection." },
    cl_waiting: {
      label: "Clients waiting",
      help: "Connected and waiting for a server connection. Anything above zero for long means the pool is too small or a query is holding a connection.",
    },
    sv_active: { label: "Servers active" },
    sv_idle: { label: "Servers idle", help: "Ready for the next client — this is the headroom." },
    sv_used: { label: "Servers used" },
    maxwait: {
      label: "Longest wait (s)",
      help: "How long the client at the front of the queue has been waiting. The single most useful number here: if it is not 0, clients are being made to wait.",
    },
    pool_mode: { label: "Mode" },
  };

  const first = (d?.pools ?? [])[0];
  const present = first ? Object.keys(first) : [];
  const ordered = [
    ...Object.keys(explained).filter((k) => present.includes(k)),
    ...present.filter((k) => !(k in explained)),
  ];

  return (
    <Section title="Connection pools" hint={d ? `${d.count}` : undefined} bodyClassName="p-0">
      <div className="p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="the connection pools">
          <>
            <DataList
              items={d?.pools ?? []}
              columns={ordered.map((k) => ({
                key: k,
                label: explained[k]?.label ?? k,
                sortable: true,
                value: (p: Record<string, string>) => p[k] ?? "",
              }))}
              getRowKey={(p) => `${p.database ?? ""}/${p.user ?? ""}`}
              emptyMessage="No pools — nothing is connecting through the pooler."
            />
            <dl className="mt-3 max-w-3xl space-y-1 text-[11.5px] leading-relaxed text-muted">
              {ordered
                .filter((k) => explained[k]?.help)
                .map((k) => (
                  <div key={k}>
                    <dt className="inline font-medium">{explained[k]?.label}: </dt>
                    <dd className="inline">{explained[k]?.help}</dd>
                  </div>
                ))}
            </dl>
          </>
        </StateView>
      </div>
    </Section>
  );
}

/** Caddy TLS. */
export function CaddyTls() {
  interface T {
    automation_policies: number;
    certificate_loaders: number;
  }
  const q = useJSON<T>("caddy-tls", "/caddy/tls");
  const d = q.data;
  return (
    <Section title="TLS">
      <StateView loading={q.isLoading} error={q.error} errorContext="Caddy TLS">
        {d && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Automation policies" value={String(d.automation_policies)} />
              <Stat label="Certificate loaders" value={String(d.certificate_loaders)} />
            </div>
            {/* An explicitly loaded certificate makes Caddy skip ACME for that name entirely, so
                a self-signed file on the shared volume permanently prevents a real one. */}
            <p className="mt-3 text-[11.5px] leading-relaxed text-muted">
              A loader pins a certificate for a name, which makes Caddy skip automatic HTTPS for it. That is correct
              for a certificate the cluster already holds and a trap for a self-signed one — it would prevent a real
              certificate being obtained for that name, permanently.
            </p>
          </>
        )}
      </StateView>
    </Section>
  );
}
